import { joiResolver } from '@hookform/resolvers/joi';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import HelpIcon from '@mui/icons-material/Help';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  styled,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { assertNotNull } from '@remote-voice/utilities';
import { ReactElement, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AdminContent } from '@/components/atoms/AdminPageParts';
import CancelButton from '@/components/atoms/CancelButton';
import LoadingBackdrop from '@/components/atoms/LoadingBackdrop';
import useLocalizedJoi from '@/components/hooks/useLocalizedJoi';
import { useSnackbar } from '@/components/hooks/useSnackbar';
import DownloadCsvDialogByRoomId from '@/components/organisms/admin/DownloadCsvDialogByRoomId';
import useConfirmDeleteDialog from '@/components/organisms/confirmDialog/useConfirmDeleteDialog';
import useHomophoneSelectorDialog from '@/components/organisms/homophoneSelectorDialog/useHomophoneSelectorDialog';
import useTemplateSelectorDialog from '@/components/organisms/templateSelectorDialog/useTemplateSelectorDialog';
import {
  TranslateMethod,
  useHomophoneCategoriesQuery,
  useRemoveChatRoomMutation,
  useTemplateMessageCategoriesQuery,
  useVadPresetsQuery,
} from '@/types/graphql';

export type ChatRoomData = {
  capacity: number;
  name: string;
  templateCategoryIds: string[];
  homophoneCategoryIds: string[];
  entryCode?: string;
  translateMethod: TranslateMethod;
  isWaitingRoomEnabled: boolean;
  isWaitingRoomNotificationEnabled: boolean;
  isGuestInputRestricted: boolean;
  isNotifyOnMessageEnabled: boolean;
  isGuideMicControlLinked: boolean;
  isGuestMicControlLinked: boolean;
  enableAutoReset: boolean;
  audioConfirmation: boolean;
  dualLangTranslation: boolean;
  vadPresetId?: string;
  isLogSavingDisabled?: boolean;
};

const MyHelpIcon = styled(HelpIcon)(({ theme }) => ({
  color: theme.palette.grey[600],
}));

export const RoomForm = (props: {
  loading: boolean;
  onSubmit: (input: ChatRoomData) => Promise<void>;
  defaultData?: ChatRoomData; // 新規登録は defaultDataなし
  children?: ReactElement;
  sessionEditable?: boolean;
  canUseSimultaneous: boolean;
  roomId?: string;
}) => {
  const { t } = useTranslation('admin');
  const { t: commonT } = useTranslation('common');
  const { t: chatT } = useTranslation('chat');
  const joi = useLocalizedJoi();

  const [showDownlaodCsvDialog, setShowDownlaodCsvDialog] = useState(false);

  const templateSelectorDialog = useTemplateSelectorDialog();
  const homophoneSelectorDialog = useHomophoneSelectorDialog();

  const {
    reset,
    register,
    handleSubmit,
    formState: { errors, isDirty },
    watch,
    setValue,
    getValues,
    control,
  } = useForm<ChatRoomData>({
    defaultValues: {
      ...props.defaultData,
      translateMethod:
        props.defaultData?.translateMethod ?? TranslateMethod.Consecutive,
      templateCategoryIds: props.defaultData?.templateCategoryIds ?? [],
      homophoneCategoryIds: props.defaultData?.homophoneCategoryIds ?? [],
      isWaitingRoomEnabled: props.defaultData?.isWaitingRoomEnabled ?? false,
      isWaitingRoomNotificationEnabled:
        props.defaultData?.isWaitingRoomNotificationEnabled ?? false,
      isGuestInputRestricted:
        props.defaultData?.isGuestInputRestricted ?? false,
      isNotifyOnMessageEnabled:
        props.defaultData?.isNotifyOnMessageEnabled ?? false,
      isGuideMicControlLinked:
        props.defaultData?.isGuideMicControlLinked ?? false,
      isGuestMicControlLinked:
        props.defaultData?.isGuestMicControlLinked ?? false,
      enableAutoReset: props.defaultData?.enableAutoReset,
      audioConfirmation: props.defaultData?.audioConfirmation ?? false,
      dualLangTranslation: props.defaultData?.dualLangTranslation,
      vadPresetId: props.defaultData?.vadPresetId ?? '',
      isLogSavingDisabled: props.defaultData?.isLogSavingDisabled ?? false,
      capacity: 0,
    },
    resolver: joiResolver(
      joi.object<ChatRoomData>({
        name: joi.string().trim().max(50).required(),
        capacity: joi.number().min(0).max(100).required(),
        templateCategoryIds: joi.array().items(joi.string().uuid()),
        homophoneCategoryIds: joi.array().items(joi.string().uuid()),
        entryCode: joi
          .string()
          .allow('')
          .pattern(/^[0-9]+$/, 'numbers')
          .trim()
          .length(4),
        translateMethod: joi
          .string()
          .valid(TranslateMethod.Consecutive, TranslateMethod.Simultaneous)
          .required(),
        isWaitingRoomEnabled: joi.boolean().required(),
        isWaitingRoomNotificationEnabled: joi.boolean().required(),
        isGuestInputRestricted: joi.boolean().required(),
        isNotifyOnMessageEnabled: joi.boolean().required(),
        isGuideMicControlLinked: joi.boolean().required(),
        isGuestMicControlLinked: joi.boolean().required(),
        enableAutoReset: joi.boolean().required(),
        audioConfirmation: joi.boolean().required(),
        dualLangTranslation: joi.boolean().required(),
        vadPresetId: joi.when('translateMethod', {
          is: TranslateMethod.Consecutive,
          then: joi.string().trim().allow('').uuid(),
          otherwise: joi.string().trim().uuid().required(),
        }),
        isLogSavingDisabled: joi.boolean().optional(),
      })
    ),
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (!props.defaultData) {
      return;
    }
    const defaultData = props.defaultData;
    reset(defaultData);
  }, [props.defaultData, reset, setValue]);

  const templateMessageCategoriesResult = useTemplateMessageCategoriesQuery({
    variables: { input: {} },
  });
  const categories =
    templateMessageCategoriesResult.data?.templateMessageCategories;

  const homophoneCategoriesResult = useHomophoneCategoriesQuery({
    variables: { input: {} },
  });
  const homophoneCategories =
    homophoneCategoriesResult.data?.homophoneCategories;

  const vadPresets = useVadPresetsQuery({ variables: { input: {} } });
  const vadPresetsLocal = vadPresets.data?.vadPresets
    ?.slice()
    .sort((a, b) => (a.label > b.label ? 1 : -1));
  const confirmDeleteDialog = useConfirmDeleteDialog();
  const [removeChatRoom, removeChatRoomResult] = useRemoveChatRoomMutation();
  const snackbar = useSnackbar();

  return (
    <>
      <Box
        component={'form'}
        onSubmit={handleSubmit(async (input, ev) => {
          ev?.preventDefault();

          // 通知の許可をリクエスト
          if (
            input.isWaitingRoomNotificationEnabled &&
            Notification.permission === 'default'
          ) {
            await Notification.requestPermission();
          }

          // 保存処理
          props.onSubmit?.(input);
        })}
        py={2}
      >
        <LoadingBackdrop open={props.loading || removeChatRoomResult.loading} />

        <AdminContent>
          <Stack spacing={2}>
            <TextField
              {...register('name')}
              label={t('room.name')}
              error={'name' in errors}
              helperText={errors.name?.message}
              InputLabelProps={{ shrink: watch('name') != '' }}
            />
          </Stack>
        </AdminContent>
        <Divider />
        <AdminContent>
          <Stack spacing={1} alignItems={'flex-start'}>
            <Stack direction={'row'} alignItems={'center'}>
              <Typography sx={{ mr: 1 }} variant="h5">
                {t('room.standardPhrasesForRoom')}
              </Typography>
              {watch('templateCategoryIds').length > 0 && (
                <>
                  <CheckCircleOutlineIcon color="primary" />
                  <Typography color={'primary'}>
                    {t('room.selected')}
                  </Typography>
                </>
              )}
            </Stack>
            <Button
              variant="outlined"
              startIcon={<MenuBookIcon />}
              onClick={async () => {
                const selectedCategories = await templateSelectorDialog.open({
                  templateCategories: categories ?? [],
                  defaultSelection: getValues().templateCategoryIds,
                });
                if (selectedCategories != null)
                  setValue('templateCategoryIds', selectedCategories, {
                    shouldDirty: true,
                  });
              }}
              sx={{ textTransform: 'none' }}
            >
              {t('room.selectTemplateMessages')}
            </Button>

            {watch('templateCategoryIds').length >= 1 && (
              <Box>
                <Typography>{`＜${t('room.selected')}＞`}</Typography>
                <Stack direction={'row'} justifyContent="flex-start">
                  {watch('templateCategoryIds').map((category, index) => (
                    <Typography key={category}>
                      {`${index < 1 ? '' : ' / '}${index + 1}.${
                        categories?.find((cat) => cat.id === category)?.name
                      }`}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </AdminContent>
        <Divider />
        <AdminContent>
          <Stack spacing={1} alignItems={'flex-start'}>
            <Stack direction={'row'} alignItems={'center'}>
              <Typography sx={{ mr: 1 }} variant="h5">
                {t('room.homophonesForRoom')}
              </Typography>
              {watch('homophoneCategoryIds').length > 0 && (
                <>
                  <CheckCircleOutlineIcon color="primary" />
                  <Typography color={'primary'}>
                    {t('room.selected')}
                  </Typography>
                </>
              )}
            </Stack>
            <Button
              variant="outlined"
              startIcon={<BookmarkIcon />}
              onClick={async () => {
                const selectedHomophoneCategories =
                  await homophoneSelectorDialog.open({
                    homophoneCategories: homophoneCategories ?? [],
                    defaultSelection: getValues().homophoneCategoryIds,
                  });
                if (selectedHomophoneCategories != null)
                  setValue(
                    'homophoneCategoryIds',
                    selectedHomophoneCategories,
                    {
                      shouldDirty: true,
                    }
                  );
              }}
              sx={{ textTransform: 'none' }}
            >
              {t('room.homophonesSelector')}
            </Button>

            {watch('homophoneCategoryIds').length >= 1 && (
              <Box>
                <Typography>{`＜${t('room.selected')}＞`}</Typography>
                <Stack direction={'row'} justifyContent="flex-start">
                  {watch('homophoneCategoryIds').map((category, index) => (
                    <Typography key={category}>
                      {`${index < 1 ? '' : ' / '}${
                        homophoneCategories?.find((cat) => cat.id === category)
                          ?.name
                      }`}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </AdminContent>
        <Divider />
        <AdminContent>
          <Stack spacing={1} alignItems={'flex-start'}>
            <Typography variant="h5">{t('room.autoResetSettings')}</Typography>
            <Controller
              control={control}
              name="enableAutoReset"
              render={({ field }) => (
                <RadioGroup row {...field}>
                  <Stack direction={'row'} alignItems={'center'} sx={{ mr: 3 }}>
                    <FormControlLabel
                      control={<Radio />}
                      label={t('room.autoResetOn')}
                      value={true}
                      sx={{ mr: 1 }}
                    />
                    <Tooltip
                      title={t('room.autoResetOnHelpMsg')}
                      arrow
                      placement="top"
                      enterTouchDelay={0}
                    >
                      <MyHelpIcon />
                    </Tooltip>
                  </Stack>
                  <Stack direction={'row'} alignItems={'center'}>
                    <FormControlLabel
                      control={<Radio />}
                      label={t('room.autoResetOff')}
                      value={false}
                      sx={{ mr: 1 }}
                    />
                    <Tooltip
                      title={t('room.autoResetOffHelpMsg')}
                      arrow
                      placement="top"
                      enterTouchDelay={0}
                    >
                      <MyHelpIcon />
                    </Tooltip>
                  </Stack>
                </RadioGroup>
              )}
            />
            <FormHelperText error={!!errors?.enableAutoReset}>
              {errors?.enableAutoReset?.message}
            </FormHelperText>
          </Stack>
        </AdminContent>
        <Divider />
        <AdminContent>
          <Stack spacing={1} alignItems={'flex-start'}>
            <Typography variant="h5">
              {t('room.dualLangTranslation')}
            </Typography>
            <Controller
              control={control}
              name="dualLangTranslation"
              render={({ field }) => (
                <RadioGroup row {...field} value={field.value?.toString()}>
                  <Stack direction={'row'} alignItems={'center'} sx={{ mr: 3 }}>
                    <FormControlLabel
                      control={<Radio />}
                      label={t('room.dualLangTranslationOff')}
                      value={false}
                      sx={{ mr: 1 }}
                    />
                    <Tooltip
                      title={t('room.dualLangTranslationOffHelpMsg')}
                      arrow
                      placement="top"
                      enterTouchDelay={0}
                    >
                      <MyHelpIcon />
                    </Tooltip>
                  </Stack>
                  <Stack direction={'row'} alignItems={'center'}>
                    <FormControlLabel
                      control={<Radio />}
                      label={t('room.dualLangTranslationOn')}
                      value={true}
                      sx={{ mr: 1 }}
                    />
                    <Tooltip
                      title={t('room.dualLangTranslationOnHelpMsg')}
                      arrow
                      placement="top"
                      enterTouchDelay={0}
                    >
                      <MyHelpIcon />
                    </Tooltip>
                  </Stack>
                </RadioGroup>
              )}
            />
            <FormHelperText error={!!errors?.dualLangTranslation}>
              {errors?.dualLangTranslation?.message}
            </FormHelperText>
          </Stack>
        </AdminContent>
        <Divider />
        <AdminContent>
          <Stack spacing={1} alignItems={'flex-start'}>
            <Typography variant="h5">{t('room.translationMethod')}</Typography>
            <Controller
              control={control}
              name="translateMethod"
              render={({ field }) => (
                <RadioGroup row {...field}>
                  <Stack direction={'row'} alignItems={'center'} sx={{ mr: 3 }}>
                    <FormControlLabel
                      control={
                        <Radio disabled={props.canUseSimultaneous === false} />
                      }
                      label={t('room.translationRealtime')}
                      value={TranslateMethod.Simultaneous}
                      sx={{ mr: 1 }}
                    />
                    <Tooltip
                      title={t('room.translationRealtimeHelpMsg')}
                      arrow
                      placement="top"
                      enterTouchDelay={0}
                    >
                      <MyHelpIcon />
                    </Tooltip>
                  </Stack>
                  <Stack direction={'row'} alignItems={'center'}>
                    <FormControlLabel
                      control={<Radio />}
                      label={t('room.translationConsective')}
                      value={TranslateMethod.Consecutive}
                      sx={{ mr: 1 }}
                    />
                    <Tooltip
                      title={t('room.translationConsectiveHelpMsg')}
                      arrow
                      placement="top"
                      enterTouchDelay={0}
                    >
                      <MyHelpIcon />
                    </Tooltip>
                  </Stack>
                </RadioGroup>
              )}
            />
          </Stack>

          <Stack mt={2} spacing={2} alignItems={'flex-start'}>
            <Controller
              name="vadPresetId"
              control={control}
              render={({ field }) => (
                <FormControl
                  disabled={
                    watch('translateMethod') === TranslateMethod.Consecutive
                  }
                  sx={{ minWidth: 200 }}
                >
                  <InputLabel id="vad-preset-label">
                    {chatT('settings.vadInputLabel')}
                  </InputLabel>
                  <Select
                    labelId="vad-preset-label"
                    label={chatT('settings.vadInputLabel')}
                    {...field}
                  >
                    {vadPresetsLocal
                      ?.sort((a) => (a.default ? -1 : 1))
                      .map((x) => (
                        <MenuItem key={x.id} value={x.id}>
                          {x.label}
                        </MenuItem>
                      )) ?? []}
                  </Select>
                  <FormHelperText
                    sx={{ color: (t) => t.palette.error.main }}
                    error={errors.vadPresetId?.message != null}
                  >
                    {errors.vadPresetId?.message}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Stack>
        </AdminContent>
        <Divider />
        <AdminContent>
          <Stack spacing={1} alignItems={'flex-start'}>
            <Typography variant="h5">{t('room.otherSettings')}</Typography>
            <Stack direction={'row'} alignItems={'center'} sx={{ pl: 1 }}>
              <Controller
                name="isLogSavingDisabled"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={!!field.value}
                        disabled={!!props.defaultData && !props.sessionEditable}
                      />
                    }
                    label={t('room.LogSavingDisabled')}
                  />
                )}
              />
            </Stack>
            <Stack direction={'row'} alignItems={'center'} sx={{ pl: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={!!props.defaultData && !props.sessionEditable}
                    checked={!!watch('entryCode')}
                    onChange={(ev) => {
                      if (ev.target.checked)
                        setValue(
                          'entryCode',
                          ('000' + Math.floor(Math.random() * 10000)).slice(-4),
                          { shouldDirty: true }
                        );
                      else
                        setValue('entryCode', undefined, { shouldDirty: true });
                    }}
                  />
                }
                label={t('room.generateEntiryCode')}
                sx={{ mr: 1 }}
              />
              <Tooltip
                title={t('room.generateEntiryCodeHelpMsg')}
                arrow
                placement="top"
                enterTouchDelay={0}
              >
                <HelpIcon sx={{ color: (t) => t.palette.grey[600] }} />
              </Tooltip>
            </Stack>
            <Stack direction={'row'} alignItems={'center'} sx={{ pl: 1 }}>
              <Controller
                name="isWaitingRoomEnabled"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        onChange={(ev, checked) => {
                          field.onChange(ev, checked);
                          if (checked === false)
                            setValue('isWaitingRoomNotificationEnabled', false);
                        }}
                        checked={field.value}
                      />
                    }
                    label={t('room.isWaitingRoomEnabled')}
                  />
                )}
              />
              <Tooltip
                title={t('room.isWaitingRoomEnabledTooltip')}
                arrow
                placement="top"
                enterTouchDelay={0}
              >
                <MyHelpIcon />
              </Tooltip>
            </Stack>
            <Stack direction={'row'} alignItems={'center'} sx={{ pl: 4 }}>
              <Controller
                name="isWaitingRoomNotificationEnabled"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    disabled={watch('isWaitingRoomEnabled') === false}
                    control={<Checkbox {...field} checked={field.value} />}
                    label={t('room.isWaitingRoomNotificationEnabled')}
                  />
                )}
              />
              <Tooltip
                title={t('room.isWaitingRoomNotificationEnabledTooltip')}
                arrow
                placement="top"
                enterTouchDelay={0}
              >
                <MyHelpIcon />
              </Tooltip>
            </Stack>
            <Stack direction={'row'} alignItems={'center'} sx={{ pl: 1 }}>
              <Controller
                name="isNotifyOnMessageEnabled"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label={t('room.isNotifyOnMessageEnabled')}
                  />
                )}
              />
              <Tooltip
                title={t('room.isNotifyOnMessageEnabledTooltip')}
                arrow
                placement="top"
                enterTouchDelay={0}
              >
                <MyHelpIcon />
              </Tooltip>
            </Stack>
            <Stack direction={'row'} alignItems={'center'} sx={{ pl: 1 }}>
              <Controller
                name="audioConfirmation"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label={t('room.audioConfirmation')}
                  />
                )}
              />
            </Stack>
          </Stack>
        </AdminContent>
        <Divider />

        <AdminContent>
          <Box
            display="grid"
            gridTemplateColumns="repeat(2, 1fr)"
            gap={2}
            maxWidth={420}
            mx="auto"
          >
            {props.defaultData && (
              <>
                <Button
                  type="button"
                  startIcon={<DownloadIcon />}
                  sx={{ width: '100%' }}
                  onClick={() => setShowDownlaodCsvDialog(true)}
                >
                  {t('room.manageLogs')}
                </Button>

                <Button
                  startIcon={<DeleteIcon />}
                  color="error"
                  sx={{ width: '100%' }}
                  onClick={async () => {
                    const name = `${props.defaultData?.name.slice(0, 27)}${
                      (props.defaultData?.name.length ?? 0) >= 28 ? '...' : ''
                    }`;

                    if (
                      await confirmDeleteDialog.open({
                        title: t('room.confirmDeleteMsg', { name }),
                        okColor: 'primary',
                      })
                    ) {
                      assertNotNull(props.roomId);
                      await removeChatRoom({
                        variables: { input: { id: props.roomId } },
                      });

                      snackbar('success', commonT('message.removed'));
                      navigate('/admin/room');
                    }
                  }}
                >
                  {t('room.delete')}
                </Button>
              </>
            )}
            <CancelButton
              onClick={() => navigate('/admin/room')}
              sx={{
                width: '100%',
                bgcolor: (t) => t.palette.common.white,
              }}
            />

            <Button type="submit" disabled={!isDirty} sx={{ width: '100%' }}>
              {props.defaultData
                ? commonT('command.saveChanges')
                : t('room.issueRoom')}
            </Button>
          </Box>
        </AdminContent>
      </Box>
      <DownloadCsvDialogByRoomId
        open={showDownlaodCsvDialog}
        onClose={() => setShowDownlaodCsvDialog(false)}
        roomId={props.roomId ?? ''}
      />
    </>
  );
};
